import { FunctionComponent } from "react";

import {
  Dialog,
  DropdownField,
  SubmissionForm,
  ToggleField,
  Heading,
  Option,
  Stack
} from "@bps/fluent-ui";
import {
  useApplicationsQuery,
  useCreateConsent
} from "@libs/api/gateways/plt-integrators/plt-integrators-gateway.hooks";
import { ConsentDto } from "@libs/api/gateways/plt-integrators/plt-integrators-gateway.dtos";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";

export interface CreateConsentDialogProps {
  tenant: Tenant;
  consents: ConsentDto[];
  onDismiss: () => void;
}

export interface CreateConsentFormValues {
  applicationId: string;
  hasAllLocationAccess: boolean;
  isEnabled: boolean;
}

export const CreateConsentDialog: FunctionComponent<CreateConsentDialogProps> = ({
  tenant,
  consents,
  onDismiss
}) => {
  const { mutateAsync: createConsent } = useCreateConsent();

  const onSubmit = async (formData: CreateConsentFormValues) => {
    await createConsent({
      applicationId: formData.applicationId,
      tenantId: tenant.id,
      hasAllLocationAccess: formData.hasAllLocationAccess,
      isEnabled: formData.isEnabled
    });
    onDismiss();
  };

  const defaultValues = {
    hasAllLocationAccess: true,
    isEnabled: true,
    applicationId: ""
  };

  const applicationsQuery = useApplicationsQuery(tenant.id);

  const applications = applicationsQuery.data
    ?.filter(
      application =>
        !consents.some(consent => consent.applicationId === application.id)
    )
    .map(applicationResult => ({
      id: applicationResult.id,
      name: applicationResult.name
    }));

  const applicationsOption: Option[] = !applicationsQuery
    ? []
    : [
        {
          key: "",
          text: "None"
        }
      ].concat(
        applications?.map(t => {
          return { key: t.id, text: t.name };
        }) ?? []
      );

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title: "Configure Consent",
        showCloseButton: true,
        onDismiss
      }}
      minWidth={450}
    >
      <SubmissionForm<CreateConsentFormValues>
        onSubmit={onSubmit}
        initialValues={defaultValues}
        hideCancelButton
      >
        {() => (
          <>
            <DropdownField
              name="applicationId"
              placeholder="Select application id"
              label="Integrator Application Id"
              options={applicationsOption}
              withNoEmptyOption={true}
            />
            <Heading labelPaddings>Integrator Application Id</Heading>
            <Stack tokens={{ childrenGap: 8 }} verticalAlign="space-between">
              <ToggleField
                name="hasAllLocationAccess"
                label="All Location Access"
                styles={{
                  root: { margin: 0, flexGrow: 1 },
                  container: { marginTop: 4 }
                }}
              />
              <ToggleField
                name="isEnabled"
                label="Enabled"
                styles={{
                  root: { margin: 0, flexGrow: 1 },
                  container: { marginTop: 4 }
                }}
              />
            </Stack>
          </>
        )}
      </SubmissionForm>
    </Dialog>
  );
};
